// Payment / Card Registration Page
// Toss Payments billing key flow:
//   1. Click "카드 등록" → Toss SDK opens secure card input UI (NOT our page)
//   2. User enters card on Toss's secure page (no Toss account needed)
//   3. Toss redirects back with authKey → backend exchanges for billingKey
//   4. BillingKey stored — charge happens after rental return

import { useState, useEffect } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import { reservationApi } from '../../lib/api';
import { useAuthStore } from '../../stores/auth';
import toast from 'react-hot-toast';

export default function PaymentPage() {

const API_BASE = window.location.hostname === 'localhost' ? '/api' : 'https://purplemine.mkim0818.workers.dev/api';
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const user = useAuthStore(s => s.user);
  const [reservation, setReservation] = useState<any>(null);
  const [loading, setLoading] = useState(true);
  const [registering, setRegistering] = useState(false);
  const [registered, setRegistered] = useState(false);
  const [cardLastFour, setCardLastFour] = useState('');

  useEffect(() => {
    if (!id) return;
    Promise.all([
      reservationApi.getById(id),
      fetch(API_BASE + '/auth/card-info', { headers: { Authorization: `Bearer ${localStorage.getItem('carp2p_token')}` } }).then(r => r.json()),
    ]).then(([res, card]: any[]) => {
      setReservation(res);
      // Check if card already registered on this reservation OR on user account
      if (res.card_last_four || card?.card_last_four) {
        setRegistered(true);
        setCardLastFour(res.card_last_four || card?.card_last_four);
      }
      setLoading(false);
    }).catch(() => setLoading(false));
  }, [id]);

  // Auto-use existing billing key for this reservation
  const handleUseExistingCard = async () => {
    if (!id) return;
    setRegistering(true);
    try {
      await reservationApi.chargePayment(id, { orderName: `카드 등록 확인 #${id.slice(0, 8)}` });
      // If charge succeeds (or billing key is valid), card is ready
      setRegistered(true);
      toast.success('기존 등록 카드로 승인 완료');
      // Auto-transition to payment_authorized via API
      const token = localStorage.getItem('carp2p_token');
      await fetch(`/api/reservations/${id}/transition`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify({ event: 'payment_authorized' }),
      });
    } catch (err: any) {
      toast.error('카드 등록이 필요합니다. 토스페이먼츠로 새 카드를 등록해주세요.');
      setRegistered(false);
    } finally {
      setRegistering(false);
    }
  };

  // Handle Toss redirect callback (authKey in URL query params)
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const authKey = params.get('authKey');
    const customerKey = params.get('customerKey');

    if (authKey && customerKey && id) {
      // Clean URL
      window.history.replaceState({}, '', window.location.pathname);
      completeRegistration(authKey, customerKey);
    }
  }, [id]);

  const completeRegistration = async (authKey: string, customerKey: string) => {
    setRegistering(true);
    try {
      const res: any = await reservationApi.registerCard(id!, { authKey, customerKey });
      setRegistered(true);
      setCardLastFour(res.card_last_four || '****');
      toast.success('카드가 등록되었습니다');
    } catch (err: any) {
      toast.error(err.message || '카드 등록 실패');
    } finally {
      setRegistering(false);
    }
  };

  // Open Toss billing key registration
  const handleTossRegister = () => {
    if (!user || !id) return;
    setRegistering(true);

    const customerKey = user.id; // UUID — unique per user
    // Build success/fail URLs for Toss redirect
    const baseUrl = window.location.origin;
    const successUrl = `${baseUrl}/reservations/${id}/payment?customerKey=${customerKey}`;
    const failUrl = `${baseUrl}/reservations/${id}/payment?error=cancel`;

    try {
      // Load Toss SDK if not loaded
      const TossPayments = (window as any).TossPayments;
      if (!TossPayments) {
        // SDK not loaded — inject it dynamically
        const script = document.createElement('script');
        script.src = 'https://js.tosspayments.com/v2';
        script.onload = () => {
          const TossPayments = (window as any).TossPayments;
          if (TossPayments) {
            TossPayments('test_ck_placeholder')
              .payment()
              .requestBillingAuth({
                method: 'CARD',
                customerKey,
                successUrl,
                failUrl,
              });
          }
        };
        document.head.appendChild(script);
      } else {
        TossPayments('test_ck_placeholder')
          .payment()
          .requestBillingAuth({
            method: 'CARD',
            customerKey,
            successUrl,
            failUrl,
          });
      }
    } catch (err: any) {
      toast.error('토스 결제 SDK 로드에 실패했습니다');
      setRegistering(false);
    }
  };

  if (loading) {
    return (
      <div className="flex justify-center py-20">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-slate-600" />
      </div>
    );
  }
  if (!reservation) {
    return <div className="text-center py-20 text-gray-400">예약을 찾을 수 없습니다</div>;
  }

  const formatPrice = (won: number) => new Intl.NumberFormat('ko-KR').format(won) + '원';
  const rentalDays = reservation.rental_days;
  const estimatedRental = reservation.total_amount;
  const estimatedCare = Math.round(estimatedRental * 0.1);
  const estimatedTotal = estimatedRental + estimatedCare;

  return (
    <div className="max-w-lg mx-auto px-4 py-8">
      <Link to={`/reservations/${id}`} className="text-sm text-gray-400 hover:text-gray-600 flex items-center gap-1 mb-6 transition-colors duration-150 focus:outline-none focus:ring-2 focus:ring-brand-500 rounded">
        <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7"/></svg>
        예약 상세로
      </Link>

      <h1 className="text-2xl font-bold text-slate-900 mb-2">결제·카드 등록</h1>
      <p className="text-sm text-gray-500 mb-6">
        카드 정보는 토스페이먼츠의 안전한 결제창에서 입력합니다. 승인만 보류되며 반납 후 최종 금액으로 청구됩니다.
      </p>

      {/* Pricing Breakdown */}
      <div className="bg-white rounded-2xl border border-gray-100 shadow-sm p-5 mb-6">
        <h2 className="font-semibold text-sm text-slate-900 mb-3">예상 금액</h2>
        <div className="space-y-2 text-sm">
          <div className="flex justify-between">
            <span className="text-gray-500">대여료 ({rentalDays}일)</span>
            <span className="font-medium">{formatPrice(estimatedRental)}</span>
          </div>
          <div className="flex justify-between">
            <span className="text-gray-500">케어·부가 예상</span>
            <span className="text-gray-400">~{formatPrice(estimatedCare)}</span>
          </div>
          <div className="border-t border-gray-100 pt-2 flex justify-between font-semibold">
            <span>예상 총액</span>
            <span className="text-slate-900">{formatPrice(estimatedTotal)}</span>
          </div>
        </div>
        <div className="mt-3 bg-amber-50 rounded-xl p-3 text-xs text-amber-700">
          ⚠️ 최종 금액은 반납 후 케어·운송·손상 등 실제 비용을 반영하여 확정됩니다. 지금은 카드 승인만 보류됩니다.
        </div>
      </div>

      {/* Card Registration — Toss SDK */}
      {registered ? (
        <div className="bg-white rounded-2xl border border-emerald-200 shadow-sm p-6 text-center">
          <div className="w-12 h-12 bg-emerald-100 rounded-full flex items-center justify-center mx-auto mb-4">
            <svg className="w-6 h-6 text-emerald-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
            </svg>
          </div>
          <h2 className="text-lg font-bold text-slate-900 mb-2">카드 등록 완료</h2>
          <p className="text-sm text-gray-500 mb-1">
            카드 정보가 토스페이먼츠에 안전하게 등록되었습니다.
          </p>
          <p className="text-lg font-mono font-bold text-slate-700 mb-4">
            •••• •••• •••• {cardLastFour}
          </p>
          <div className="bg-amber-50 rounded-xl p-3 text-xs text-amber-700 mb-4">
            승인 보류 상태 — 반납 후 최종 금액 청구 예정
          </div>
          <button
            onClick={() => navigate(`/reservations/${id}`)}
            className="w-full bg-slate-900 hover:bg-slate-800 text-white font-semibold py-3 rounded-xl text-sm transition-colors focus:outline-none focus:ring-2 focus:ring-brand-500"
          >
            예약 상세로 돌아가기
          </button>
        </div>
      ) : (
        <div className="bg-white rounded-2xl border border-gray-100 shadow-sm p-6">
          <h2 className="font-semibold text-sm text-slate-900 mb-4">카드 등록</h2>

          <div className="bg-blue-50 rounded-xl p-4 mb-5 text-sm text-blue-800 space-y-2">
            <p className="font-medium">안전한 카드 등록</p>
            <ul className="space-y-1 text-blue-700 text-xs">
              <li>· 카드 정보는 토스페이먼츠 결제창에서 직접 입력합니다</li>
              <li>· 당사는 카드 정보를 저장하지 않습니다</li>
              <li>· 토스페이먼츠 가입이 필요 없습니다</li>
              <li>· 등록 즉시 100원 결제 후 취소로 유효성 검증</li>
            </ul>
          </div>

          {/* Billing agreement */}
          <div className="p-3 bg-gray-50 rounded-xl text-xs text-gray-500 space-y-2 mb-5">
            <p>카드를 등록하면 아래 사항에 동의하는 것으로 간주됩니다:</p>
            <ul className="list-disc list-inside space-y-1">
              <li>반납 후 확정된 최종 금액이 자동 청구됩니다</li>
              <li>추가 손상·연체 발생 시 사전 고지 후 청구될 수 있습니다</li>
              <li>청구 내역은 예약 상세에서 확인 가능합니다</li>
            </ul>
          </div>

          <button
            onClick={handleTossRegister}
            disabled={registering}
            className="w-full flex items-center justify-center gap-2 bg-[#3182f6] hover:bg-[#1b64da] text-white font-semibold py-3.5 rounded-xl text-sm transition-colors disabled:opacity-50 focus:outline-none focus:ring-2 focus:ring-brand-500"
          >
            {registering ? (
              <>처리 중...</>
            ) : (
              <>
                <span className="text-lg font-bold">T</span>
                <span>토스페이먼츠 카드 등록</span>
              </>
            )}
          </button>

          <p className="text-xs text-center text-gray-400 mt-3">
            토스페이먼츠 결제창이 열리며, 카드 등록 완료 후 자동으로 돌아옵니다.
          </p>
        </div>
      )}
    </div>
  );
}
